import React, { useState, Fragment } from 'react';
import { UncontrolledPopover, PopoverBody } from 'reactstrap';
import { keyBy } from 'lodash-es';
import { SiteSettings } from 'src/scripts/utils/environment';
import PixleeAnalytics from '../../../utils/pixlee-analytics';
import { InstaProductInterface, IproductsShopifyData, PixleeAlbum } from './pixlee-types';
import { notNil } from 'src/scripts/utils/typescriptUtils';
import { toLocalCurrencyShowEstimate } from '../../../utils/currency';

interface IPopoverTypes {
  placement:
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end';
  boundariesElement: HTMLElement | null | string;
  flip: boolean;
  container?: HTMLElement | null;
}

const PopOver = ({
  id,
  item,
  product,
  productsShopifyData,
  albumId,
  albumPhotoId,
}: {
  id: string;
  item: IPopoverTypes;
  product: InstaProductInterface;
  productsShopifyData?: IproductsShopifyData;
  albumId: number;
  albumPhotoId: number;
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const handle = product.sku.replaceAll('-', '_');
  const shopifyPrice = productsShopifyData && productsShopifyData[handle]?.shopifyPrice;

  const toggle = () => setPopoverOpen(!popoverOpen);

  const productSkuUnderscore = product.sku?.replace(/-/g, '_');

  const productLink =
    productsShopifyData?.[productSkuUnderscore] && product.sku?.includes('-simple')
      ? `/products/${product.sku.replace('-simple', '')}`
      : product.link.replace('https://www.aloyoga.com', '').replace('https://www.alo.com', '') || '';

  const sendPixleeAnalyticsOnOpenPopover = () => {
    try {
      PixleeAnalytics.sendDataOnOpenLightbox({ albumId: albumId?.toString(), albumPhotoId: albumPhotoId?.toString() });
    } catch (error) {
      console.log(error);
    }
  };

  const sendPixleeAnalyticsOnProductClick = () => {
    try {
      PixleeAnalytics.sendDataOnProductClick({
        albumId: albumId?.toString(),
        albumPhotoId: albumPhotoId?.toString(),
        actionLinkUrl: productLink,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const parentProductStyleId = window.server_pdp_data ? window.server_pdp_data.product.handle.split('-')[0] : null;
  const productStyleId = product.sku?.split('-')[0];
  const isStyleIdMatchesParent = productStyleId === parentProductStyleId;

  const onlineStoreUrl = productsShopifyData?.[productSkuUnderscore]?.onlineStoreUrl;
  const shouldHide = SiteSettings.pixleeShouldHideUnpublishedProducts === true && typeof onlineStoreUrl !== 'string';
  const shouldShow = !shouldHide;

  return (
    <span style={shouldShow ? undefined : { display: 'none' }} data-handle={productSkuUnderscore}>
      <Fragment>
        <button
          className="post__tag-button"
          type="button"
          id={`Popover-${id}`}
          onClick={sendPixleeAnalyticsOnOpenPopover}
        >
          <span className={popoverOpen ? 'post__tag active' : 'post__tag'} />
        </button>
        <UncontrolledPopover
          class="pixlee-feed-popover"
          className="pixlee-feed-popover"
          placement={item.placement}
          isOpen={popoverOpen}
          target={`Popover-${id}`}
          toggle={toggle}
          boundariesElement={item.boundariesElement}
          trigger="legacy"
          flip={item.flip}
          container={item.container}
        >
          <PopoverBody class="pixlee-feed-popover" className="pixlee-feed-popover">
            <a
              href={productLink}
              className="popover__product-link"
              onClick={(e) => {
                isStyleIdMatchesParent ? e.preventDefault() : sendPixleeAnalyticsOnProductClick();
              }}
              style={{ cursor: isStyleIdMatchesParent ? 'default' : 'pointer' }}
            >
              <div className="popover__wrapper">
                <img src={product.image} className="img-fluid popover__image" alt="" />
                <div className="popover__center">
                  <p className="product-name">{product.title}</p>
                  {shopifyPrice && (
                    <p className="product-price" data-currency={!isStyleIdMatchesParent ? true : null}>
                      {isStyleIdMatchesParent ? '(This item)' : `${toLocalCurrencyShowEstimate(shopifyPrice)}`}
                    </p>
                  )}
                </div>
                {!isStyleIdMatchesParent && (
                  <img
                    src="https://cdn.shopify.com/s/files/1/2185/2813/files/caret-right-popover.svg?v=1583795669"
                    alt=""
                    aria-hidden="true"
                  />
                )}
              </div>
            </a>
          </PopoverBody>
        </UncontrolledPopover>
      </Fragment>
    </span>
  );
};

const InstaPostPopovers = ({
  data,
  item,
  productsShopifyData,
}: {
  data: PixleeAlbum;
  item: IPopoverTypes;
  productsShopifyData?: IproductsShopifyData;
}) => {
  const boundingBoxMap = keyBy(data.bounding_box_products, ({ product_id }) => product_id);

  return (
    <>
      {data.products
        .map((product) => {
          const boundingBox = boundingBoxMap[product.id];
          if (!boundingBox) return null;
          const { y, x, height, width } = boundingBox;
          const heightFraction = (height || 0) / data.height;
          const widthFraction = (width || 0) / data.width;
          return (
            <div
              style={{
                top: `${((y || 0) / data.height) * 100}%`,
                left: `${((x || 0) / data.width) * 100}%`,
                height: `${heightFraction === 1 ? heightFraction : heightFraction * 100}%`,
                width: `${widthFraction === 1 ? widthFraction : widthFraction * 100}%`,
              }}
              className="post__tag-box"
              data-id={product.id}
              key={product.id}
            >
              <PopOver
                item={item}
                id={`${product.id}-${data.id}`}
                product={product}
                productsShopifyData={productsShopifyData}
                albumId={data.album_id}
                albumPhotoId={data.album_photo_id}
              />
            </div>
          );
        })
        .filter(notNil)}
    </>
  );
};

export default InstaPostPopovers;
